import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import './BirthdayGreeting.css';

// ==========================================
// CELEBRATION COMPONENTS
// ==========================================

const Banners = ({ userName }) => {
    const text = "FELIZ CUMPLEAÑOS";
    const colors = ['#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];
    const name = userName ? userName.toUpperCase() : "";
    
    return (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none', zIndex: 10, paddingTop: '30px' }}>
            {/* Row 1: Banner */}
            <div className="birthday-banners">
                <div className="birthday-banners-rope" style={{ animation: 'dropDown 1s ease-out both' }}></div>
                {text.split('').map((char, i) => {
                    if (char === ' ') return <div key={i} style={{ width: '20px' }}></div>;
                    return (
                        <div 
                            key={i} 
                            className="pennant"
                            style={{ 
                                backgroundColor: colors[i % colors.length],
                                animationDelay: `${i * 0.1}s, ${1.2 + (i * 0.1)}s` // Staggered entry and sway
                            }}
                        >
                            {char}
                        </div>
                    );
                })}
            </div>

            {/* Row 2: Name in Foil Balloons */}
            {name && (
                <div className="foil-balloons-row">
                    {name.split('').map((char, i) => {
                        if (char === ' ') return <div key={i} style={{ width: '30px' }}></div>;
                        const tilt = (i % 2 === 0 ? 3 : -3) + (i % 3);
                        return (
                            <div 
                                key={i} 
                                className="foil-balloon-wrapper"
                                style={{ animationDelay: `${i * 0.2}s` }}
                            >
                                <div style={{ transform: `rotate(${tilt}deg)`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <span className="foil-text" style={{ animation: 'birthdayFadeIn 0.5s both', animationDelay: `${0.8 + (i * 0.15)}s` }}>
                                        {char}
                                    </span>
                                    <div className="foil-tail" style={{ animation: 'birthdayFadeIn 0.5s both', animationDelay: `${0.8 + (i * 0.15)}s` }}></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const Balloons = () => {
    const colors = ['#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'];
    const balloons = Array.from({ length: 25 });

    return (
        <div className="balloons-container">
            {balloons.map((_, i) => {
                const left = Math.random() * 100;
                const delay = Math.random() * 3;
                const duration = 5 + Math.random() * 5;
                const scale = 0.6 + Math.random() * 0.6;

                return (
                    <div
                        key={i}
                        className="balloon-wrapper"
                        style={{
                            left: `${left}%`,
                            animation: `floatUp ${duration}s ease-in ${delay}s forwards`,
                        }}
                    >
                        <div 
                            className="balloon"
                            style={{
                                backgroundColor: colors[Math.floor(Math.random() * colors.length)],
                                transform: `scale(${scale})`
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const BirthdayGreeting = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [showOverlay, setShowOverlay] = useState(false);
    const [isClosingOverlay, setIsClosingOverlay] = useState(false);
    const [isBirthday, setIsBirthday] = useState(false);
    const [isBelated, setIsBelated] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        if (!user) return;

        // Extract birth date from available profiles
        const birthDateStr = user.personal?.fecha_nacimiento || 
                           user.alumno?.fecha_nacimiento || 
                           user.apoderado_info?.fecha_nacimiento;

        if (!birthDateStr) return;

        const today = dayjs();
        // Force parsing only the date part (YYYY-MM-DD)
        const bDayBase = dayjs(birthDateStr.split('T')[0]); 
        const bDayThisYear = bDayBase.year(today.year());

        // Check Case 1: Today is the birthday
        const isActualBirthday = today.isSame(bDayThisYear, 'day');
        
        // Check Case 2: Yesterday was the birthday (the 'belated' greeting)
        // Note: Subtracting 1 day from today to see if it was the birthday
        const yesterday = today.subtract(1, 'day');
        const isDayAfterBirthday = yesterday.isSame(bDayBase.year(yesterday.year()), 'day');

        if (isActualBirthday || isDayAfterBirthday) {
            setIsBirthday(true);
            setIsBelated(isDayAfterBirthday);

            // The 'instance' we are celebrating is the birthday of that specific year
            const celebratedInstanceYear = isActualBirthday ? today.year() : yesterday.year();
            const lastCelebrationYear = localStorage.getItem('birthday_celebrated_instance_year');
            const celebratedYearStr = String(celebratedInstanceYear);

            if (lastCelebrationYear !== celebratedYearStr) {
                // Add a small delay
                const delayTimer = setTimeout(() => {
                    setShowOverlay(true);
                    localStorage.setItem('birthday_celebrated_instance_year', celebratedYearStr);
                    
                    // Play audio
                    try {
                        const audio = new Audio('https://actions.google.com/sounds/v1/crowds/battle_crowd_celebrate.ogg'); 
                        audio.volume = 0.4;
                        audio.play().catch(() => {});
                        audioRef.current = audio;
                    } catch (e) {}
                }, 1000);

                const closeTimer = setTimeout(() => closeOverlay(), 11000);

                return () => {
                    clearTimeout(delayTimer);
                    clearTimeout(closeTimer);
                };
            }
        } else {
            setIsBirthday(false);
            setIsBelated(false);
        }
    }, [user, location.pathname]);

    const closeOverlay = () => {
        setIsClosingOverlay(true);
        
        // Fade out audio
        if (audioRef.current && !audioRef.current.paused) {
            let vol = audioRef.current.volume;
            const fadeOutInterval = setInterval(() => {
                if (vol > 0.05) {
                    vol -= 0.05;
                    audioRef.current.volume = vol;
                } else {
                    audioRef.current.pause();
                    clearInterval(fadeOutInterval);
                }
            }, 200);
        }

        // Unmount after animation
        setTimeout(() => {
            setShowOverlay(false);
            setIsClosingOverlay(false);
        }, 1000);
    };

    if (!isBirthday) return null;

    return (
        <>
            {showOverlay && (
                <div className={`birthday-overlay ${isClosingOverlay ? 'fade-out' : 'fade-in'}`}>
                    <Banners userName={user?.firstName} />
                    <Balloons />
                    
                    <div className="birthday-card">
                        <h2 className="birthday-title">¡Muchas Felicidades!</h2>
                        <p className="birthday-message" style={{ fontSize: '1rem', marginTop: '5px' }}>
                            Te deseamos que este nuevo año de tu vida esté lleno de éxitos, alegría y momentos inolvidables. ¡Lo mejor para ti!
                        </p>
                        <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', marginTop: '15px' }}>
                            De parte de todo el equipo de <strong>Orion Internacional</strong>.
                        </p>
                        <button 
                            onClick={closeOverlay}
                            className="birthday-button"
                        >
                            Continuar
                        </button>
                    </div>
                </div>
            )}

            {/* Persistent subtle indicator for the rest of the day */}
            {!showOverlay && (
                <div 
                    className="persistent-balloon" 
                    title="¡Clic para ver el saludo de nuevo!"
                    onClick={() => setShowOverlay(true)}
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                >
                    <div className="balloon" style={{ backgroundColor: '#EF4444', transform: 'scale(0.8)' }}>
                        <div className="persistent-balloon-text">
                            ¡Feliz<br/>Día!
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BirthdayGreeting;
